import config from 'src/config'
import shopConfig from 'template/config'
import type { MenuPageBuilderExternalRel } from 'src/shared/dbTypes/menuPageBuilder'

export const isExternalHref = (href: string) => /^(https?:)?\/\//.test(href)

export const isAnchorOnlyHref = (href: string) =>
  isExternalHref(href) || href.startsWith('mailto:') || href.startsWith('tel:')

const normalizeDomain = (value?: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/^www\./, '')

const getHrefDomain = (href: string) => {
  try {
    const url = href.startsWith('//')
      ? new URL(`https:${href}`)
      : new URL(href)

    return normalizeDomain(url.hostname)
  } catch (_error) {
    return ''
  }
}

const trustedExternalDomains = [
  process.env.NEXT_PUBLIC_HOST,
  config.siteURL,
  ...((shopConfig as { trustedExternalDomains?: readonly string[] }).trustedExternalDomains || []),
]
  .map(normalizeDomain)
  .filter(Boolean)

const isTrustedExternalHref = (href: string) => {
  const domain = getHrefDomain(href)
  if (!domain) return false

  return trustedExternalDomains.some(trustedDomain =>
    domain === trustedDomain || domain.endsWith(`.${trustedDomain}`)
  )
}

export const getExternalLinkRel = (
  href: string,
  externalRel: MenuPageBuilderExternalRel = 'auto'
) => {
  if (!isExternalHref(href)) return undefined

  const baseRel = ['noopener', 'noreferrer']

  if (externalRel === 'follow') {
    return baseRel.join(' ')
  }

  if (externalRel === 'sponsored') {
    return [...baseRel, 'sponsored', 'nofollow'].join(' ')
  }

  if (externalRel === 'nofollow') {
    return [...baseRel, 'nofollow'].join(' ')
  }

  return isTrustedExternalHref(href)
    ? baseRel.join(' ')
    : [...baseRel, 'nofollow'].join(' ')
}
